/**
 * OrderDocumentsCard — iter174
 *
 * Zeigt inline unter der Käufer-Bestellübersicht die zu dieser Bestellung
 * gehörigen Dokumente (Bestellübersicht, ggf. Erstattungsbelege). Der User
 * kann jedes Dokument einzeln als PDF herunterladen. Automatischer
 * E-Mail-Versand läuft parallel im Backend — Card ist nur der Download-Punkt.
 */
import { useEffect, useState } from "react";
import { FileText, Download, Loader2, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

const TYPE_LABELS = {
  ORDER_CONFIRMATION: "Bestellübersicht",
  SELLER_PAYOUT_STATEMENT: "Verkäufer-Abrechnung",
  REFUND_RECEIPT: "Erstattungsbeleg",
  PAYOUT_ADJUSTMENT: "Abrechnungskorrektur",
  SUBSCRIPTION_CONFIRMATION: "Abo-Bestätigung",
  SUBSCRIPTION_RENEWAL: "Verlängerungsbestätigung",
  SUBSCRIPTION_CANCELLATION: "Kündigungsbestätigung",
};

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
}

export default function OrderDocumentsCard({ orderId }) {
  const { t } = useTranslation();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/me/documents?order_id=${orderId}`);
        if (!alive) return;
        setDocs(r.data?.documents || []);
      } catch (e) {
        if (!alive) return;
        setErr("Dokumente konnten nicht geladen werden.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [orderId]);

  const download = async (doc) => {
    setDownloadingId(doc.document_id);
    try {
      const r = await api.get(`/documents/${doc.document_id}/download`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || `${doc.document_number}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setErr("Download fehlgeschlagen.");
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) return null;
  if (!docs.length && !err) return null;

  return (
    <div className="px-4 pb-4" data-testid="order-documents-card">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-1.5">{t("order.your_documents")}</p>
      <div className="border border-[#E5E5E5] rounded-lg divide-y divide-[#F0F0F0]">
        {err && (
          <div className="p-3 flex items-center gap-2 text-xs text-red-700 bg-red-50">
            <AlertCircle size={14} /> {err}
          </div>
        )}
        {docs.map((d) => (
          <div
            key={d.document_id}
            data-testid={`order-document-${d.document_id}`}
            className="p-3 flex items-center gap-3"
          >
            <FileText size={18} className="text-[#0A0A0A] shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#0A0A0A] truncate">
                {TYPE_LABELS[d.document_type] || d.document_type}
              </p>
              <p className="text-[11px] text-[#888] truncate">
                {d.document_number} · {fmtDate(d.created_at)}
              </p>
            </div>
            <button
              onClick={() => download(d)}
              disabled={downloadingId === d.document_id}
              data-testid={`order-document-download-${d.document_id}`}
              className="h-8 px-3 text-[11px] font-semibold bg-[#0A0A0A] text-white flex items-center gap-1.5 hover:bg-[#222] disabled:opacity-50"
            >
              {downloadingId === d.document_id
                ? <Loader2 size={12} className="animate-spin" />
                : <Download size={12} />}
              PDF
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
